import { DatePipe } from '@angular/common';
import { Component, OnDestroy, OnInit, effect, inject, input, viewChild } from '@angular/core';
import { FormGroup, ReactiveFormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { provideNativeDateAdapter } from '@angular/material/core';
import { MatDateRangePicker, MatDatepicker, MatDatepickerModule } from '@angular/material/datepicker';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatTimepicker, MatTimepickerModule, MatTimepickerOption } from '@angular/material/timepicker';
import { TranslateModule } from '@ngx-translate/core';
import { IconComponent } from '@shared/components/icon/icon.component';
import { MatErrorComponent } from '@shared/components/mat-error/mat-error.component';
import { DatePickerUtilitiesService } from '@utilities/date-picker-utilities.service';
import { DateUtilitiesService } from '@utilities/date-utilities.service';
import { TimepickerUtilitiesService } from '@utilities/timepicker-utilities.service';
import { endOfDay, isBefore, startOfDay } from 'date-fns';
import { Subscription } from 'rxjs';
import { IDateTimeRangeForm } from './date-time-range-fields.interface';

/**
 * Generic start/end date+time range picker - a decoupled copy of `app-campaign-dates`.
 *
 * `app-campaign-dates` can't be reused directly outside the campaign form: it's typed to
 * `FormGroup<ICampaignForm>`, takes a `campaignStatus` input for its own draft-only "past start"
 * warning, and (the real risk) its `CampaignDatesService` is a `providedIn: 'root'` singleton that
 * pushes start/end into shared `BehaviorSubject`s - two open forms using the real component at once
 * would stomp each other's global date state. This copy drops that service, the campaign-only
 * minimum-duration check, and the draft-status warning, keeping only the linked range-calendar +
 * time-picker mechanics against a plain `FormGroup<IDateTimeRangeForm>`.
 */
@Component({
  imports: [
    ReactiveFormsModule,
    MatInputModule,
    MatFormFieldModule,
    MatDatepickerModule,
    MatTimepickerModule,
    MatErrorComponent,
    TranslateModule,
    MatButtonModule,
    IconComponent,
    DatePipe,
  ],
  providers: [provideNativeDateAdapter()],
  selector: 'app-date-time-range-fields',
  templateUrl: './date-time-range-fields.component.html',
})
export class DateTimeRangeFieldsComponent implements OnInit, OnDestroy {
  public readonly form = input.required<FormGroup<IDateTimeRangeForm>>();

  // When true, each field shows its label as a Material floating label instead of the
  // group heading + inline placeholder.
  public readonly floatingLabels = input<boolean>(true);

  // Minimum required gap between start and end - editing either side that would leave less than
  // this many minutes between them pushes the other side away to keep the gap, so end can never
  // land before (or too close to) start.
  public readonly minimumGapMinutes = input<number>(60);

  public allTimeOptions: MatTimepickerOption<Date>[] = [];

  public startTimeFilteredOptions: MatTimepickerOption<Date>[] = [];
  public endTimeFilteredOptions: MatTimepickerOption<Date>[] = [];

  public readonly datePickerUtilities = inject(DatePickerUtilitiesService);
  private readonly dateUtils = inject(DateUtilitiesService);
  private readonly timepickerUtilities = inject(TimepickerUtilitiesService);

  private readonly startRangePicker = viewChild.required<MatDateRangePicker<Date>>('startRangePicker');
  private readonly endDatePicker = viewChild.required<MatDatepicker<Date>>('endDatePicker');

  private readonly subscriptions: Subscription[] = [];

  private isSyncingPicker = false;

  constructor() {
    // Required signal inputs aren't guaranteed available synchronously in the constructor when the
    // component is created imperatively (e.g. via MatDialog/CDK portal), so this defers the read to
    // an effect - same reason `CampaignDatesComponent` (which this was copied from) does the same.
    effect(() => {
      const form = this.form();
      const startDate = form.get('startDate')?.value;
      const endDate = form.get('endDate')?.value;

      if (startDate === null || startDate === undefined) {
        form.get('startDate')?.setValue(startOfDay(new Date()), { emitEvent: false });
        form.get('startTime')?.setValue(startOfDay(new Date()), { emitEvent: false });
      }

      if (endDate === null || endDate === undefined) {
        form.get('endDate')?.setValue(endOfDay(new Date()), { emitEvent: false });
        form.get('endTime')?.setValue(endOfDay(new Date()), { emitEvent: false });
      }
    });
  }

  public ngOnInit(): void {
    this.subscribeToDateTimeChanges();
    this.subscribeToPickerEvents();
    this.allTimeOptions = this.timepickerUtilities.generateTimeOptions();
    this.startTimeFilteredOptions = [...this.allTimeOptions];
    this.endTimeFilteredOptions = [...this.allTimeOptions];

    this.syncPickerSelections(true);
  }

  public openStartPicker(): void {
    if (this.endDatePicker()) {
      this.endDatePicker().close();
    }

    setTimeout(() => this.startRangePicker().open());
  }

  public openEndPicker(): void {
    if (this.startRangePicker()) {
      this.startRangePicker().close();
    }

    setTimeout(() => this.endDatePicker().open());
  }

  public onRangeStartDateChange(date: Date | null): void {
    if (!date) {
      return;
    }

    this.form().get('startDate')?.setValue(date);

    const startTimeValue: Date | null = this.form()?.get('startTime')?.value as Date | null;

    if (startTimeValue) {
      this.form()?.get('startTime')?.setValue(this.dateUtils.combineDateTime(date, startTimeValue));
    }
  }

  public onRangeEndDateChange(date: Date | null): void {
    const validEndDate: Date | null = this.datePickerUtilities.getValidEndDate(
      date,
      (this.form().get('startDate')?.value as Date | null) ?? null,
    );

    if (!validEndDate) {
      return;
    }

    this.form().get('endDate')?.setValue(validEndDate);

    const endTimeValue: Date | null = this.form()?.get('endTime')?.value as Date | null;

    if (endTimeValue) {
      this.form()?.get('endTime')?.setValue(this.dateUtils.combineDateTime(validEndDate, endTimeValue));
    }
  }

  public onInputChange(event: Event, timepicker: MatTimepicker<Date>): void {
    const inputElement = event.target as HTMLInputElement;
    const inputValue = inputElement?.value || '';

    this.startTimeFilteredOptions = this.timepickerUtilities.filterTimeOptions(inputValue, this.allTimeOptions);
    this.endTimeFilteredOptions = this.timepickerUtilities.filterTimeOptions(inputValue, this.allTimeOptions);

    timepicker.close();
    setTimeout(() => timepicker.open());
  }

  public selectAllText(event: FocusEvent): void {
    const timeInput = event.target as HTMLInputElement;
    timeInput.select();
  }

  public ngOnDestroy(): void {
    this.unsubscribeAll();
  }

  private subscribeToDateTimeChanges(): void {
    this.subscribeToStartDateTimeChanges();
    this.subscribeToEndDateTimeChanges();
  }

  private subscribeToStartDateTimeChanges(): void {
    const startDateControl = this.form().get('startDate');

    if (startDateControl) {
      this.subscriptions.push(
        startDateControl.valueChanges.subscribe((value) => {
          if (this.form()?.get('endDate')?.value) {
            this.form()?.get('endDate')?.markAsTouched();
          }

          const startTimeValue = this.form()?.get('startTime')?.value;

          if (!startTimeValue && value) {
            this.form()?.get('startTime')?.setValue(startOfDay(value));
          }

          if (startTimeValue && value) {
            this.form()?.get('startTime')?.setValue(this.dateUtils.combineDateTime(value, startTimeValue));
          }

          this.enforceMinimumGap('start');
        }),
      );
    }

    const startTimeControl = this.form().get('startTime');

    if (startTimeControl) {
      this.subscriptions.push(
        startTimeControl.valueChanges.subscribe(() => {
          if (this.form()?.get('endTime')?.value) {
            this.form()?.get('endTime')?.markAsTouched();
          }

          this.enforceMinimumGap('start');
        }),
      );
    }
  }

  private subscribeToEndDateTimeChanges(): void {
    const endDateControl = this.form().get('endDate');

    if (endDateControl) {
      this.subscriptions.push(
        endDateControl.valueChanges.subscribe((value) => {
          const normalizedEndDate: Date | null = this.datePickerUtilities.getValidEndDate(
            value as Date | null,
            (this.form().get('startDate')?.value as Date | null) ?? null,
          );

          if (value && normalizedEndDate && value.getTime() !== normalizedEndDate.getTime()) {
            this.form()?.get('endDate')?.setValue(normalizedEndDate, { emitEvent: false });
          }

          const endTimeValue = this.form()?.get('endTime')?.value;

          if (!endTimeValue && normalizedEndDate) {
            this.form()?.get('endTime')?.setValue(endOfDay(normalizedEndDate));
          }

          if (endTimeValue && normalizedEndDate) {
            this.form()?.get('endTime')?.setValue(this.dateUtils.combineDateTime(normalizedEndDate, endTimeValue));
          }

          this.enforceMinimumGap('end');
        }),
      );
    }

    const endTimeControl = this.form().get('endTime');

    if (endTimeControl) {
      this.subscriptions.push(endTimeControl.valueChanges.subscribe(() => this.enforceMinimumGap('end')));
    }
  }

  // Keeps end at least `minimumGapMinutes` after start, regardless of whether that means the same
  // day or not - pushes whichever side wasn't just edited so the invariant always holds.
  private enforceMinimumGap(editedSide: 'start' | 'end'): void {
    const startDate = this.form().get('startDate')?.value as Date | null;
    const startTime = this.form().get('startTime')?.value as Date | null;
    const endDate = this.form().get('endDate')?.value as Date | null;
    const endTime = this.form().get('endTime')?.value as Date | null;

    if (!startDate || !startTime || !endDate || !endTime) {
      return;
    }

    const start = this.dateUtils.combineDateTime(startDate, startTime);
    const end = this.dateUtils.combineDateTime(endDate, endTime);
    const minimumGapMs = this.minimumGapMinutes() * 60 * 1000;

    if (end.getTime() - start.getTime() >= minimumGapMs) {
      return;
    }

    if (editedSide === 'end') {
      const newStart = new Date(end.getTime() - minimumGapMs);

      this.form().get('startDate')?.setValue(startOfDay(newStart), { emitEvent: false });
      this.form().get('startTime')?.setValue(newStart, { emitEvent: false });
    } else {
      const newEnd = new Date(start.getTime() + minimumGapMs);

      this.form().get('endDate')?.setValue(startOfDay(newEnd), { emitEvent: false });
      this.form().get('endTime')?.setValue(newEnd, { emitEvent: false });
    }
  }

  private subscribeToPickerEvents(): void {
    if (this.startRangePicker()) {
      this.subscriptions.push(
        this.startRangePicker().openedStream.subscribe(() => this.syncPickerSelections()),
        this.startRangePicker().closedStream.subscribe(() => {
          this.ensureEndDateWhenOnlyStartSelected();
        }),
      );
    }

    if (this.endDatePicker()) {
      this.subscriptions.push(
        this.endDatePicker().openedStream.subscribe(() => this.syncPickerSelections()),
        this.endDatePicker().closedStream.subscribe(() => {
          this.ensureEndDateWhenOnlyStartSelected();
        }),
      );
    }
  }

  private unsubscribeAll(): void {
    this.subscriptions?.forEach((subscription) => subscription?.unsubscribe());
    this.subscriptions.length = 0;
  }

  private syncPickerSelections(initialSync = false): void {
    if (this.isSyncingPicker) {
      return;
    }

    this.isSyncingPicker = true;

    const startDate: Date | null = (this.form().get('startDate')?.value as Date | null) || null;
    const endDate: Date | null = (this.form().get('endDate')?.value as Date | null) || null;

    setTimeout(() => {
      this.applySelectionToPicker(this.startRangePicker(), startDate, endDate);
      this.isSyncingPicker = false;

      this.form().controls.startDate.setValue(startDate);
      this.form().controls.endDate.setValue(endDate);

      if (initialSync) {
        this.form().controls.startDate.markAsPristine();
        this.form().controls.endDate.markAsPristine();
        this.form().controls.startDate.markAsUntouched();
        this.form().controls.endDate.markAsUntouched();
      }
    });
  }

  private applySelectionToPicker(
    picker: MatDateRangePicker<Date> | undefined,
    start: Date | null,
    end: Date | null,
  ): void {
    if (start) {
      picker?.select(start);
    }

    if (end) {
      picker?.select(end);
    }
  }

  private ensureEndDateWhenOnlyStartSelected(): void {
    const startDate = (this.form().get('startDate')?.value as Date | null) || null;
    const endDate = (this.form().get('endDate')?.value as Date | null) || null;

    if (startDate && !endDate) {
      this.form().get('endDate')?.setValue(startDate);
    }

    if (startDate && endDate && isBefore(startOfDay(endDate), startOfDay(startDate))) {
      this.form().get('endDate')?.setValue(startDate);
    }

    this.syncPickerSelections();
  }
}
